import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { countSongsForDevice, currentDeviceCode, looksLikeDeviceCode, saveDeviceCode } from '../deviceRecovery';

/**
 * DeviceRestore — **恢復我嘅歌**（共用元件；`我的` 全版 ＋ 日記空狀態 精簡版）
 *
 * ⭐ 2026-09-23 產品負責人實測（iPhone）：**主畫面 app 同 Safari 係分開儲存（各自身份）**，
 *   而且 **iOS 唔容許 link 直接開主畫面 app**（Android 會）→ 所以「貼返恢復碼」係 iOS 唯一通道。
 *   因此：① 呢個區塊一定要喺 **library 空嘅時候都見到**（唔可以收埋喺「我的」）
 *        ② 要一句講清楚平台差異，令用戶唔會以為「又壞」
 *
 * 誠實規則（`rules.md` B2）：**0 首／打唔到 API 一律唔准寫入**，要照實講原因。
 */

/** 開場 URL 恢復失敗時，`App.tsx` 會放低原因（讀完即清；唔會重複顯示） */
export function takeRestoreFailure(): string | null {
  try {
    const failed = sessionStorage.getItem('cosingery.restoreFailed');
    sessionStorage.removeItem('cosingery.restoreFailed');
    return failed;
  } catch {
    return null;
  }
}

const card: React.CSSProperties = {
  background: 'var(--color-card)',
  border: '1px solid var(--color-divider)',
  borderRadius: '18px',
  padding: '16px',
  marginTop: '14px',
  textAlign: 'left',
};
const h2: React.CSSProperties = { margin: '0 0 8px', fontSize: 'var(--font-size-title, 22px)' };
const btn: React.CSSProperties = {
  minHeight: '40px',
  padding: '0 14px',
  borderRadius: '12px',
  border: '1.5px solid var(--color-divider)',
  background: 'var(--color-card)',
  color: 'var(--color-text)',
  fontFamily: 'inherit',
  fontSize: 'var(--font-size-button, 18px)',
  cursor: 'pointer',
};

export default function DeviceRestore({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(() => {
    const failed = takeRestoreFailure();
    if (failed === 'empty') return t('restore_failed_empty');
    if (failed === 'offline') return t('restore_failed_offline');
    return null;
  });
  const myCode = currentDeviceCode();
  /**
   * ⭐ 2026-09-23#4：**顯示「呢部機而家」有幾多首** ——
   *   為何：產品負責人真中過——佢複製咗**本機嗰個新碼**（0 首）去貼 → 見到「搵唔到歌」，
   *   以為資料冇咗。顯示數量就一眼睇得出「呢部機係空嘅，要貼**另一嗰個**碼」。
   *   `undefined` ＝ 查緊；`null` ＝ 查唔到（照實講）
   */
  const [myCount, setMyCount] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    if (!myCode) {
      setMyCount(null);
      return;
    }
    let alive = true;
    setMyCount(undefined);
    void countSongsForDevice(myCode).then((n) => {
      if (alive) setMyCount(n);
    });
    return () => {
      alive = false;
    };
  }, [myCode]);

  /** 貼返恢復碼 → **驗證有歌**才寫入（0 首／打唔到 API 照實講；見 `deviceRecovery.ts`） */
  const doRestore = async () => {
    const value = code.trim();
    if (!looksLikeDeviceCode(value)) {
      setNote(t('restore_invalid'));
      return;
    }
    setBusy(true);
    setNote(null);
    const n = await countSongsForDevice(value);
    setBusy(false);
    if (n === null) {
      setNote(t('restore_failed_offline'));
      return;
    }
    if (n === 0) {
      /** ⭐ 2026-09-23#4：如果佢貼嘅就係**本機嗰個**碼 → 講得更白（真中過嘅誤會） */
      setNote(value === myCode ? t('restore_failed_same_code') : t('restore_failed_empty'));
      return;
    }
    saveDeviceCode(value);
    setNote(t('restore_ok'));
    window.location.replace('/songs');
  };

  const copy = () => {
    if (!myCode) return;
    void navigator.clipboard?.writeText(myCode).catch(() => undefined);
    setNote(t('restore_copied'));
  };

  return (
    <section style={card} data-testid="me-restore">
      <h2 style={h2}>{t(compact ? 'restore_card_title' : 'restore_title')}</h2>
      <p className="footnote" style={{ margin: '0 0 6px' }}>
        {t(compact ? 'restore_card_body' : 'restore_warn')}
      </p>
      {/* ⭐ iPhone 平台事實：主畫面 app 同 Safari 分開儲存 ＋ iOS 唔容許 link 開 app */}
      <p className="footnote" style={{ margin: '0 0 6px' }} data-testid="restore-ios-note">
        {t('restore_ios_note')}
      </p>
      <p className="footnote" style={{ margin: '0 0 8px' }}>
        {t('restore_emergency_hint')}{' '}
        <a href="/recover.html" style={{ color: 'var(--color-text)' }}>
          /recover.html
        </a>
      </p>

      <p style={{ margin: '0 0 6px' }}>
        {t('restore_code_label')}：
        <code data-testid="me-device-code" style={{ wordBreak: 'break-all', fontSize: '0.85em' }}>
          {myCode || t('restore_no_code')}
        </code>
        {/* ⭐ #4：**呢部機而家**有幾多首（一眼睇得出空唔空） */}
        <span data-testid="me-device-count" style={{ marginLeft: '6px', fontWeight: 700 }}>
          {myCount === undefined
            ? t('restore_busy')
            : myCount === null
              ? t('restore_own_count_unknown')
              : t('restore_own_count', { n: myCount })}
        </span>
      </p>
      <p className="footnote" style={{ margin: '0 0 8px' }} data-testid="restore-which-code">
        {t('restore_which_code_hint')}
      </p>
      <button type="button" data-testid="me-restore-copy" style={btn} disabled={!myCode} onClick={copy}>
        {t('restore_copy')}
      </button>

      <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
        <input
          type="text"
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          data-testid="me-restore-input"
          placeholder={t('restore_input_placeholder')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ ...btn, flex: '1 1 200px', textAlign: 'left', fontWeight: 400, cursor: 'text' }}
        />
        <button
          type="button"
          data-testid="me-restore-submit"
          style={btn}
          disabled={busy || !code.trim()}
          onClick={() => void doRestore()}
        >
          {busy ? t('restore_busy') : t('restore_button')}
        </button>
      </div>
      {note ? (
        <p className="footnote" style={{ margin: '6px 0 0' }} data-testid="me-restore-note" role="status">
          {note}
        </p>
      ) : null}
    </section>
  );
}
